import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { DialogActions } from '@mui/material';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { MetadataError } from '@ep/error-handling';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { BodyText, Subheading } from '../../shared/typography/PrimaryTypography';
import GhostButton from '../../shared/button/GhostButton';
import PrimaryButton from '../../shared/button/PrimaryButton';
import DeleteIcon from '@mui/icons-material/Delete';

interface UndoImportConfirmationModalProps {
  open: boolean;
  import_label?: string | null;
  import_created?: string | null;
  handleCloseUndoModal: () => void;
  handleUndoImport: (() => Promise<void>) | null;
}

/**
 * A model confirming the undoing of a data import.
 * @param {boolean} open The display state of the modal.
 * @param {string} import_label The display name of the import to be shown in the title.
 * @param {string} import_created The created date of the import to be shown in the title.
 * @param {(): void} handleCloseUndoModal Handler to close the modal.
 * @param {(): Promise<void>} handleUndoImport Confirm undoing of import and call function from import type.
 * @returns {JSX.Element}
 */
const UndoImportConfirmationModal = ({
  open,
  import_label,
  import_created,
  handleCloseUndoModal,
  handleUndoImport
}: UndoImportConfirmationModalProps) => {
  const [undoLoading, setUndoLoading] = React.useState<boolean>(false);
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);

  const handleUndoClick = async (): Promise<void> => {
    setUndoLoading(true);
    try {
      handleUndoImport && (await handleUndoImport());
      handleCloseUndoModal();
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: UndoImportConfirmationModal failed on an unknown error while calling handleUndoClick.',
          null,
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to undo import. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setUndoLoading(false);
    }
    return;
  };

  return (
    <Dialog open={open} onClose={handleCloseUndoModal}>
      <Subheading
        weight="Bold"
        sx={{ m: 2 }}
      >{`Are you sure you want to undo Import: ${import_label} | ${import_created || ''}?`}</Subheading>
      <DialogContent>
        <br />
        <BodyText id="undo-explainer" textAlign={'justify'}>
          All entities that were imported as part of import: {import_label} | {import_created || ''}{' '}
          will be deleted forever.
        </BodyText>
        <br />
        <DialogActions sx={{ mt: 2 }}>
          <GhostButton dark onClick={handleCloseUndoModal}>
            Cancel
          </GhostButton>
          <PrimaryButton
            startIcon={<DeleteIcon />}
            loading={undoLoading}
            onClick={async () => {
              await handleUndoClick();
            }}
            palette_color="error"
          >
            UNDO
          </PrimaryButton>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default UndoImportConfirmationModal;
